// Project archive: ONE zip holding a revision's takeoff file plus its drawing files, so a takeoff and the drawings
// it was measured on travel together (hand-off, backup, moving to another site).
//
//   takeoff.cstakeoff.json   the takeoff file (utils/takeoffFile.js) — rooms, conditions, overrides, sheet setup
//   manifest.json            { format, version, drawings:[{ sheetId, file, mime, name, sha256 }] }
//   drawings/NN-<name>.<ext> each drawing's original file (PDF / PNG / JPEG), byte for byte
//
// Built in the browser from the drawings the page has already loaded; nothing is uploaded. The zip is STORE-only
// (no compression — PDFs are already compressed and this needs no dependency), which also makes the format trivial
// to read back. Our own writer and reader only; a zip made by another tool with compression is refused with a
// plain message rather than guessed at.
//
// Safety: every entry is size- and type-checked on the way in (G42) — only PDF / PNG / JPEG drawings, a total cap,
// an entry-count cap, CRC-32 verified, file names never used as paths. Nothing here touches the database.

export const ARCHIVE_FORMAT = "cleanspace-takeoff-archive";
export const ARCHIVE_VERSION = 1;
export const ARCHIVE_EXTENSION = ".cstakeoff.zip";
export const ARCHIVE_CAP_BYTES = 150 * 1024 * 1024;      // total drawing bytes in one archive
export const MAX_ARCHIVE_ENTRIES = 600;
export const TAKEOFF_ENTRY = "takeoff.cstakeoff.json";
export const MANIFEST_ENTRY = "manifest.json";
// the same limits the drawing loader enforces (data-URL lengths) — an archive cannot smuggle in a bigger file
export const PDF_MAX_DATAURL = 9_000_000;
export const IMAGE_MAX_DATAURL = 4_500_000;

const MIME_EXT = { "application/pdf": ".pdf", "image/png": ".png", "image/jpeg": ".jpg" };
const EXT_MIME = { ".pdf": "application/pdf", ".png": "image/png", ".jpg": "image/jpeg", ".jpeg": "image/jpeg" };

// ── CRC-32 ───────────────────────────────────────────────────────────────────
let CRC_TABLE = null;
function crcTable() {
  if (CRC_TABLE) return CRC_TABLE;
  CRC_TABLE = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    CRC_TABLE[n] = c >>> 0;
  }
  return CRC_TABLE;
}
export function crc32(bytes) {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

// ── data URLs <-> bytes ──────────────────────────────────────────────────────
export function parseDataUrl(dataUrl) {
  const m = /^data:([^;,]+)?((?:;[^;,]+)*?),/.exec(String(dataUrl || ""));
  if (!m) return null;
  const isB64 = /;base64/i.test(m[2] || "");
  return { mime: (m[1] || "").toLowerCase(), base64: isB64, headerLength: m[0].length };
}
export function dataUrlToBytes(dataUrl) {
  const p = parseDataUrl(dataUrl);
  if (!p || !p.base64) return null;
  const bin = atob(dataUrl.slice(p.headerLength));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return { mime: p.mime, bytes: out };
}
export function bytesToDataUrl(bytes, mime) {
  let bin = "";
  const CH = 0x8000;
  for (let i = 0; i < bytes.length; i += CH) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + CH));
  return `data:${mime};base64,${btoa(bin)}`;
}
/** Decoded size of a base64 data URL without decoding it. */
export function dataUrlBytes(dataUrl) {
  const p = parseDataUrl(dataUrl);
  if (!p) return 0;
  const body = dataUrl.length - p.headerLength;
  let pad = 0;
  if (dataUrl.endsWith("==")) pad = 2; else if (dataUrl.endsWith("=")) pad = 1;
  return Math.max(0, Math.floor((body * 3) / 4) - pad);
}

// ── zip (store-only) ─────────────────────────────────────────────────────────
const enc = new TextEncoder();
const dec = new TextDecoder("utf-8");
function dosTime(d) { return ((d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1)) & 0xffff; }
function dosDate(d) { return (((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate()) & 0xffff; }

/** entries: [{ name, data: Uint8Array }] -> Uint8Array (a zip, no compression). */
export function buildZip(entries, now = new Date()) {
  const t = dosTime(now), dt = dosDate(now);
  const parts = [];
  const central = [];
  let offset = 0;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const crc = crc32(e.data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); local.setUint16(4, 20, true); local.setUint16(6, 0x0800, true); local.setUint16(8, 0, true);
    local.setUint16(10, t, true); local.setUint16(12, dt, true); local.setUint32(14, crc, true);
    local.setUint32(18, e.data.length, true); local.setUint32(22, e.data.length, true); local.setUint16(26, name.length, true); local.setUint16(28, 0, true);
    parts.push(new Uint8Array(local.buffer), name, e.data);
    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true); cen.setUint16(4, 20, true); cen.setUint16(6, 20, true); cen.setUint16(8, 0x0800, true); cen.setUint16(10, 0, true);
    cen.setUint16(12, t, true); cen.setUint16(14, dt, true); cen.setUint32(16, crc, true);
    cen.setUint32(20, e.data.length, true); cen.setUint32(24, e.data.length, true); cen.setUint16(28, name.length, true);
    cen.setUint16(30, 0, true); cen.setUint16(32, 0, true); cen.setUint16(34, 0, true); cen.setUint16(36, 0, true); cen.setUint32(38, 0, true); cen.setUint32(42, offset, true);
    central.push(new Uint8Array(cen.buffer), name);
    offset += 30 + name.length + e.data.length;
  }
  const cdSize = central.reduce((n, p) => n + p.length, 0);
  const eocd = new DataView(new ArrayBuffer(22));
  eocd.setUint32(0, 0x06054b50, true); eocd.setUint16(8, entries.length, true); eocd.setUint16(10, entries.length, true);
  eocd.setUint32(12, cdSize, true); eocd.setUint32(16, offset, true);
  const all = [...parts, ...central, new Uint8Array(eocd.buffer)];
  const out = new Uint8Array(all.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of all) { out.set(p, at); at += p.length; }
  return out;
}

export const looksLikeZip = (bytes) => !!bytes && bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;

/** -> { ok: true, entries: Map<name, Uint8Array> } | { ok: false, error }. STORE entries only; CRC verified. */
export function readZip(bytes, { maxEntries = MAX_ARCHIVE_ENTRIES, maxTotalBytes = ARCHIVE_CAP_BYTES + 64 * 1024 * 1024 } = {}) {
  if (!looksLikeZip(bytes)) return { ok: false, error: "That is not a zip file." };
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 65535); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) return { ok: false, error: "The zip file is damaged (no directory at its end)." };
  const count = view.getUint16(eocd + 10, true);
  const cdOffset = view.getUint32(eocd + 16, true);
  if (count > maxEntries) return { ok: false, error: `The archive has ${count} files; at most ${maxEntries} are accepted.` };
  const entries = new Map();
  let p = cdOffset, total = 0;
  for (let n = 0; n < count; n++) {
    if (p + 46 > bytes.length || view.getUint32(p, true) !== 0x02014b50) return { ok: false, error: "The zip file is damaged (bad directory entry)." };
    const method = view.getUint16(p + 10, true);
    const crc = view.getUint32(p + 16, true);
    const csize = view.getUint32(p + 20, true), usize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true), extraLen = view.getUint16(p + 30, true), commentLen = view.getUint16(p + 32, true);
    const localAt = view.getUint32(p + 42, true);
    const name = dec.decode(bytes.subarray(p + 46, p + 46 + nameLen));
    p += 46 + nameLen + extraLen + commentLen;
    if (name.endsWith("/")) continue;
    if (method !== 0) return { ok: false, error: `"${name}" is compressed. Only archives made by CleanSpace One (uncompressed) can be imported — re-export from CleanSpace One rather than re-zipping.` };
    if (csize !== usize) return { ok: false, error: `"${name}" is damaged (size mismatch).` };
    total += usize;
    if (total > maxTotalBytes) return { ok: false, error: "The archive is larger than the accepted limit." };
    if (localAt + 30 > bytes.length || view.getUint32(localAt, true) !== 0x04034b50) return { ok: false, error: `"${name}" is damaged (bad file header).` };
    const lName = view.getUint16(localAt + 26, true), lExtra = view.getUint16(localAt + 28, true);
    const start = localAt + 30 + lName + lExtra;
    if (start + usize > bytes.length) return { ok: false, error: `"${name}" is damaged (truncated).` };
    const data = bytes.subarray(start, start + usize);
    if (crc32(data) !== crc) return { ok: false, error: `"${name}" failed its integrity check — the archive is damaged.` };
    entries.set(name, data);
  }
  return { ok: true, entries };
}

// ── what goes in ─────────────────────────────────────────────────────────────
const sheetLabel = (s) => [s.SheetNumber, s.SheetName].filter(Boolean).join(" — ") || s.FileName || `Drawing ${s.SheetID}`;
const safeName = (t) => String(t || "drawing").normalize("NFKD").replace(/[^\w.\- ]+/g, "").replace(/\s+/g, "_").replace(/^[._]+/, "").slice(0, 60) || "drawing";

/**
 * The picker's rows: every drawing with a file, its size, whether it fits. Drawings without a file are listed as
 * unavailable. `selected` (Set of SheetIDs) defaults to everything that fits, in order, up to the cap.
 * -> { items:[{ sheetId, label, bytes, mime, available, reason, included }], includedBytes, overCap }
 */
export function planArchive({ sheets = [], selected = null, capBytes = ARCHIVE_CAP_BYTES } = {}) {
  const items = sheets.map(s => {
    const p = s.BackgroundImage ? parseDataUrl(s.BackgroundImage) : null;
    const bytes = p ? dataUrlBytes(s.BackgroundImage) : 0;
    const ext = p ? MIME_EXT[p.mime] : null;
    let reason = null;
    if (!s.BackgroundImage) reason = "no drawing file loaded";
    else if (String(s.BackgroundImage).startsWith("blob://")) reason = "kept in drawing storage — not in archives yet";
    else if (!p || !p.base64 || !ext) reason = "unsupported file type";
    return { sheetId: s.SheetID, label: sheetLabel(s), bytes, mime: p?.mime || null, available: !reason, reason, included: false };
  });
  let used = 0;
  for (const it of items) {
    if (!it.available) continue;
    const want = selected ? selected.has(it.sheetId) : true;
    if (want && used + it.bytes <= capBytes) { it.included = true; used += it.bytes; }
    else if (want) it.reason = "would exceed the archive size limit";
  }
  return { items, includedBytes: used, overCap: items.some(i => i.available && (selected ? selected.has(i.sheetId) : true) && !i.included) };
}

/**
 * -> Uint8Array. `takeoffText` is the serialized takeoff file; `sheets` the sheets to include (already chosen).
 */
export function buildArchive({ takeoffText, sheets = [], now = new Date() }) {
  const entries = [{ name: TAKEOFF_ENTRY, data: enc.encode(takeoffText) }];
  const drawings = [];
  sheets.forEach((s, i) => {
    const d = s.BackgroundImage ? dataUrlToBytes(s.BackgroundImage) : null;
    if (!d || !MIME_EXT[d.mime]) return;
    const file = `drawings/${String(i + 1).padStart(2, "0")}-${safeName(s.FileName ? s.FileName.replace(/\.[^.]+$/, "") : sheetLabel(s))}${MIME_EXT[d.mime]}`;
    entries.push({ name: file, data: d.bytes });
    drawings.push({ sheetId: s.SheetID, file, mime: d.mime, name: sheetLabel(s), fileName: s.FileName || null, sha256: s.FileHash || null, bytes: d.bytes.length });
  });
  const manifest = { format: ARCHIVE_FORMAT, version: ARCHIVE_VERSION, createdAt: now.toISOString(), drawings };
  entries.splice(1, 0, { name: MANIFEST_ENTRY, data: enc.encode(JSON.stringify(manifest, null, 1)) });
  return { bytes: buildZip(entries, now), manifest };
}

export function archiveFileName({ project = "", revision = "", date = new Date() } = {}) {
  const clean = (t) => String(t || "").replace(/[^\w\- ]+/g, "").trim().replace(/\s+/g, "_").slice(0, 40);
  const stamp = date.toISOString().slice(0, 10);
  return [clean(project) || "takeoff", clean(revision), stamp].filter(Boolean).join("_") + ARCHIVE_EXTENSION;
}

// ── what comes out ───────────────────────────────────────────────────────────
/**
 * Read an archive. -> { ok, takeoffText, drawings: Map<sheetId, { sheetId, name, fileName, mime, bytes, sha256, dataUrl(), tooLarge }>,
 * warnings } | { ok:false, error }. The data URL is built on demand (it is ~1.4x the file's size).
 */
export function parseArchive(bytes) {
  const z = readZip(bytes);
  if (!z.ok) return z;
  const takeoff = z.entries.get(TAKEOFF_ENTRY);
  if (!takeoff) return { ok: false, error: "This zip is not a CleanSpace One takeoff archive (it has no takeoff file)." };
  let manifest = null;
  const mRaw = z.entries.get(MANIFEST_ENTRY);
  if (mRaw) { try { manifest = JSON.parse(dec.decode(mRaw)); } catch { return { ok: false, error: "The archive's manifest is damaged." }; } }
  if (!manifest || manifest.format !== ARCHIVE_FORMAT) return { ok: false, error: "This zip is not a CleanSpace One takeoff archive." };
  if (!Number.isInteger(manifest.version) || manifest.version > ARCHIVE_VERSION) return { ok: false, error: "This archive was made by a newer version of CleanSpace One." };
  const warnings = [];
  const drawings = new Map();
  let total = 0;
  for (const d of Array.isArray(manifest.drawings) ? manifest.drawings : []) {
    const data = z.entries.get(d?.file);
    if (!data) { warnings.push(`The archive lists "${d?.name || d?.file}" but does not contain it.`); continue; }
    const ext = String(d.file).slice(String(d.file).lastIndexOf(".")).toLowerCase();
    const mime = EXT_MIME[ext];
    if (!mime) { warnings.push(`"${d.name || d.file}" is not a PDF, PNG or JPEG and was left out.`); continue; }
    total += data.length;
    if (total > ARCHIVE_CAP_BYTES) { warnings.push(`"${d.name || d.file}" and later drawings were left out: the archive holds more than ${Math.round(ARCHIVE_CAP_BYTES / 1048576)} MB of drawings.`); break; }
    // the stored limits the drawing loader applies, in data-URL characters
    const est = Math.ceil(data.length / 3) * 4 + 30;
    const tooLarge = est > (mime === "application/pdf" ? PDF_MAX_DATAURL : IMAGE_MAX_DATAURL);
    drawings.set(d.sheetId, { sheetId: d.sheetId, name: d.name || d.file, fileName: d.fileName || null, mime, bytes: data.length, sha256: d.sha256 || null, tooLarge, dataUrl: () => bytesToDataUrl(data, mime) });
  }
  return { ok: true, takeoffText: dec.decode(takeoff), drawings, warnings };
}
